<template>
  <div>
    <b-form @submit.prevent>
      <b-row class="mb-3">
        <b-col>
          <b-form-group label="Service ID:">
            <b-form-input v-model="form.serviceId" readonly></b-form-input>
          </b-form-group>
        </b-col>
      </b-row>
      <!-- POD Selection -->
      <b-card>
        <pod-selector
          :available-pods="availablePodsForThisPerimeter"
          :selected-pods-list="form.podRequestList"
          @pods-selected="handlePodsSelected"
          @pod-toggled="onPodToggled"
          ref="podSelector"
        />

        <!-- POD List -->
        <h5>POD Request List</h5>
        <b-table
          :items="form.podRequestList"
          :fields="podFields"
          class="mt-3"
          striped
          hover
          responsive
        >
          <template #cell(actions)="data">
            <b-button size="sm" variant="danger" @click="removePod(data.index)">
              Remove
            </b-button>
          </template>
        </b-table>

        <!-- Power Section -->
        <div class="mt-4">
          <h5>Power</h5>
          <b-card class="mb-3">
            <b-row>
              <b-col md="2">
                <b-form-group label="Start Index (0-95)">
                  <b-input-group>
                    <b-form-input
                      v-model.number="bulkPower.startIndex"
                      type="number"
                      min="0"
                      max="95"
                    ></b-form-input>
                    <b-input-group-append>
                        <div class="readonly-disabled grey-class">
                          {{ calculateTimeInterval(bulkPower.startIndex) }}
                        </div>
                    </b-input-group-append>
                  </b-input-group>
                </b-form-group>
              </b-col>
              <b-col md="2">
                <b-form-group label="End Index (0-95)">
                  <b-input-group>
                  <b-form-input
                    v-model.number="bulkPower.endIndex"
                    type="number"
                    min="0"
                    max="95"
                  ></b-form-input>
                  <b-input-group-append>
                    <div class="readonly-disabled grey-class">
                      {{ calculateTimeInterval(bulkPower.endIndex) }}
                    </div>
                  </b-input-group-append>
                    </b-input-group>
                </b-form-group>
              </b-col>
              <b-col md="2">
                <b-form-group label="Power Up (kW)">
                  <b-form-input
                    v-model.number="bulkPower.powerUp"
                    type="number"
                    step="0.01"
                  ></b-form-input>
                </b-form-group>
              </b-col>
              <b-col md="2">
                <b-form-group label="Price Up (€/kWh)">
                  <b-form-input
                    v-model.number="bulkPower.priceUp"
                    type="number"
                    step="0.01"
                  ></b-form-input>
                </b-form-group>
              </b-col>
              <b-col md="2">
                <b-form-group label="Power Down (kW)">
                  <b-form-input
                    v-model.number="bulkPower.powerDown"
                    type="number"
                    step="0.01"
                    readonly
                    disabled
                  ></b-form-input>
                </b-form-group>
              </b-col>
              <b-col md="2">
                <b-form-group label="Price Down (€/kWh)">
                  <b-form-input
                    v-model.number="bulkPower.priceDown"
                    type="number"
                    step="0.01"
                    readonly
                    disabled
                  ></b-form-input>
                </b-form-group>
              </b-col>
            </b-row>
            <b-button @click="addBulkPower" class="mt-2 orange-class">
              Add Interval
            </b-button>
          </b-card>

          <b-table
            :items="form.power"
            :fields="powerFields"
            striped
            hover
            responsive
          >
            <template #cell(timeInterval)="data">
              {{ calculateTimeInterval(data.item.index) }}
            </template>

            <template #cell(pUp)="data">
              <b-form-input
                v-model.number="data.item.pUp"
                type="number"
                step="0.01"
              ></b-form-input>
            </template>

            <template #cell(pDown)="data">
              <b-form-input
                v-model.number="data.item.pDown"
                type="number"
                step="0.01"
              ></b-form-input>
            </template>

            <template #cell(pUpPrice)="data">
              <b-form-input
                v-model.number="data.item.pUpPrice"
                type="number"
                step="0.01"
              ></b-form-input>
            </template>

            <template #cell(pDownPrice)="data">
              <b-form-input
                v-model.number="data.item.pDownPrice"
                type="number"
                step="0.01"
              ></b-form-input>
            </template>

            <template #cell(actions)="data">
              <b-button
                size="sm"
                variant="danger"
                @click="removePower(data.index)"
              >
                Remove
              </b-button>
            </template>
          </b-table>

          <b-modal
            v-model="showAddPowerModal"
            title="Add Power Entry"
            @ok="handleAddPowerSubmit"
          >
            <b-form>
              <b-form-group label="Index (0-95)">
                <b-form-input
                  v-model.number="newPowerEntry.index"
                  type="number"
                  min="0"
                  max="95"
                  required
                ></b-form-input>
              </b-form-group>

              <b-form-group label="Time Interval">
                <b-form-input
                  :value="
                    newPowerEntry.index !== null
                      ? calculateTimeInterval(newPowerEntry.index)
                      : ''
                  "
                  readonly
                  disabled
                ></b-form-input>
              </b-form-group>

              <b-form-group label="P Up (kW)">
                <b-form-input
                  v-model.number="newPowerEntry.pUp"
                  type="number"
                  step="0.01"
                ></b-form-input>
              </b-form-group>

              <b-form-group label="P Up Price (€/kWh)">
                <b-form-input
                  v-model.number="newPowerEntry.pUpPrice"
                  type="number"
                  step="0.01"
                ></b-form-input>
              </b-form-group>

              <b-form-group label="P Down (kW)">
                <b-form-input
                  v-model.number="newPowerEntry.pDown"
                  type="number"
                  step="0.01"
                  readonly
                  disabled
                ></b-form-input>
              </b-form-group>

              <b-form-group label="P Down Price (€/kWh)">
                <b-form-input
                  v-model.number="newPowerEntry.pDownPrice"
                  type="number"
                  step="0.01"
                  readonly
                  disabled
                ></b-form-input>
              </b-form-group>
            </b-form>
          </b-modal>

          <!-- Bottone per aggiungere singola riga -->
          <b-button @click="addPower" class="mt-2 blue-class">
            Add power entry
          </b-button>
        </div>
      </b-card>
    </b-form>
  </div>
</template>

<script>
import PodSelector from "./PodSelector.vue"

export default {
  name: "PerimeterForm",

  components: {
    PodSelector
  },

  props: {
    marketSessionId: {
      type: String,
      required: true
    },
    marketType: {
      type: String,
      required: true,
      default: "DAY_AHEAD",
      validator: function (value) {
        console.log("Validating marketType:", value)
        return (
          ["DAY_AHEAD", "REAL_TIME", "dayAhead", "realTime"].indexOf(value) !==
          -1
        )
      }
    },
    startActivation: {
      type: String,
      required: true
    },
    availablePods: {
      type: Array,
      required: true
    },
    serviceId: {
      type: String,
      required: true
    },
    allSelectedPods: {
      type: null,
      required: true,
      default: () => new Set()
    },
    perimeterIndex: {
      type: Number,
      required: true
    },
    initialData: {
      type: Object,
      default: null
    }
  },

  data () {
    return {
      form: {
        serviceId: "",
        podRequestList: [],
        power: []
      },
      selectedPod: null,
      podFields: [
        { key: "pod", label: "POD" },
        { key: "baselineId", label: "Baseline ID" },
        { key: "aggregatorId", label: "Aggregator ID" },
        { key: "upperP", label: "upperP (kW)" },
        { key: "upperQ", label: "upperQ (kW)" },
        { key: "actions", label: "Actions" }
      ],
      bulkPower: {
        startIndex: null,
        endIndex: null,
        powerUp: null,
        priceUp: null,
        powerDown: null,
        priceDown: null
      },
      newPowerEntry: {
        index: null,
        pUp: "",
        pDown: "",
        pUpPrice: "",
        pDownPrice: ""
      },
      showAddPowerModal: false,
      powerFields: [
        { key: "timeInterval", label: "Time Interval" },
        { key: "index", label: "Index" },
        { key: "pUp", label: "P Up (kW)" },
        { key: "pDown", label: "P Down (kW)" },
        { key: "pUpPrice", label: "P Up Price (€/kWh)" },
        { key: "pDownPrice", label: "P Down Price (€/kWh)" },
        { key: "actions", label: "Actions" }
      ]
    }
  },

  computed: {
    selectedPodsSet () {
      return this.allSelectedPods instanceof Set
        ? this.allSelectedPods
        : new Set(this.allSelectedPods)
    },
    availablePodsForThisPerimeter () {
      const currentPods = new Set(this.form.podRequestList.map((p) => p.pod))
      return this.availablePods.filter(
        (pod) => !this.allSelectedPods.has(pod.pod) || currentPods.has(pod.pod)
      )
    }
  },

  mounted () {
    if (this.serviceId) {
      this.form.serviceId = this.serviceId
      console.log("Setting serviceId in mounted:", this.serviceId)
    }
    this.$on("update-available-pods", () => {
      this.$emit("pods-updated")
    })
  },

  watch: {
    initialData: {
      immediate: true,
      handler (newData) {
        if (newData) {
          // Popola il form con i dati iniziali
          this.form = {
            serviceId: newData.serviceId,
            podRequestList: newData.podRequestList.map(pod => ({
              pod: pod.pod,
              baselineId: pod.baselineId,
              aggregatorId: pod.aggregatorId,
              upperP: pod.upperP,
              upperQ: pod.upperQ,
              productReferenceList: pod.productReferenceList || []
            })),
            power: newData.power.map(p => ({
              index: p.index,
              pUp: p.pUp / 1000, // Converti da W a kW
              pDown: p.pDown / 1000,
              pUpPrice: (p.pUpPrice / 100 / 0.25 / 1000),
              pDownPrice: (p.pDownPrice / 100 / 0.25 / 1000),
              qUp: p.qUp / 1000,
              qDown: p.qDown / 1000,
              qUpPrice: (p.qUpPrice / 100 / 0.25 / 1000),
              qDownPrice: (p.qDownPrice / 100 / 0.25 / 1000)
            }))
          }
        }
      }
    },
    serviceId: {
      handler (newVal) {
        if (newVal) {
          console.log("Setting serviceId in watcher:", newVal)
          this.form.serviceId = newVal
        }
      },
      immediate: true
    },
    editingPerimeters: {
      deep: true,
      handler () {
        this.updateAvailablePods()
      }
    }
  },

  methods: {
    handlePodsSelected (pods) {
    // Mantieni i pod esistenti e aggiungi i nuovi
      const existingPods = new Set(this.form.podRequestList.map((p) => p.pod))
      const newPods = pods.filter((pod) => !existingPods.has(pod.pod))

      this.form.podRequestList = [
        ...this.form.podRequestList,
        ...newPods.map((pod) => ({
          pod: pod.pod,
          baselineId: pod.baselineId,
          aggregatorId: pod.aggregatorId,
          upperP: pod.upperP,
          upperQ: pod.upperQ,
          productReferenceList: []
        }))
      ]

      newPods.forEach(pod => {
        this.$emit('pod-permanently-selected', pod.pod)
      })

      this.$emit("perimeter-pods-updated", this.form.podRequestList)
      this.$emit("update:selected-pods", this.form.podRequestList)
    },

    updatePods (pods) {
      if (this.$refs.podSelector) {
        this.$refs.podSelector.updateAvailablePods(pods)
      }
    },

    removePod (index) {
      const removedPod = this.form.podRequestList[index]
      this.form.podRequestList.splice(index, 1)

      // Deseleziona il pod dalla tabella
      if (this.$refs.podSelector) {
        this.$refs.podSelector.unselectPod(removedPod.pod)
      }

      // Emetti evento per ripristinare il pod nella lista checkabile
      this.$emit("pod-restored", removedPod)
    },

    addPower () {
      this.showAddPowerModal = true
    },

    removePower (index) {
      this.form.power.splice(index, 1)
    },

    calculateTimeInterval (index) {
      const totalMinutes = index * 15
      const hours = Math.floor(totalMinutes / 60)
        .toString()
        .padStart(2, "0")
      const minutes = (totalMinutes % 60).toString().padStart(2, "0")
      return `${hours}:${minutes}`
    },

    getFormData () {
      return {
        ...this.form,
        power: this.form.power.map((p) => ({
          ...p,
          pUp: p.pUp * 1000,
          pDown: p.pDown * 1000,
          pUpPrice: p.pUpPrice * 100 * 0.25 * 1000,
          pDownPrice: p.pDownPrice * 100 * 0.25 * 1000,
          qUp: p.qUp * 1000,
          qDown: p.qDown * 1000,
          qUpPrice: p.qUpPrice * 100 * 0.25 * 1000,
          qDownPrice: p.qDownPrice * 100 * 0.25 * 1000
        }))
      }
    },

    resetForm () {
      this.form = {
        podRequestList: [],
        power: []
      }
      this.selectedPod = null
    },

    validate () {
      if (!this.form.podRequestList.length) {
        throw new Error("At least one POD must be added")
      }

      if (!this.form.power.length) {
        throw new Error("At least one power entry must be added")
      }

      // Validate power entries
      for (const power of this.form.power) {
        if (!power.pUp || !power.pUpPrice) {
          throw new Error(
            "All power entries must have P Up and P Up Price values"
          )
        }
      }

      return true
    },
    addBulkPower () {
      const { startIndex, endIndex, powerUp, priceUp, powerDown, priceDown } =
        this.bulkPower

      // Validation
      if (!this.validateBulkPowerInput()) {
        return
      }

      // Creating new records
      for (let i = startIndex; i <= endIndex; i++) {
        const existingIndex = this.form.power.findIndex((p) => p.index === i)

        const powerEntry = {
          index: i,
          pUp: powerUp,
          pDown: powerDown,
          pUpPrice: priceUp,
          pDownPrice: priceDown,
          qUp: 0,
          qDown: 0,
          qUpPrice: 0,
          qDownPrice: 0
        }

        if (existingIndex >= 0) {
          // Update existing record
          this.form.power[existingIndex] = powerEntry
        } else {
          // Add new record
          this.form.power.push(powerEntry)
        }
      }

      // Sort by index
      this.form.power.sort((a, b) => a.index - b.index)

      // Reset form
      this.resetBulkPowerForm()
    },

    validateBulkPowerInput () {
      const { startIndex, endIndex, powerUp, priceUp } = this.bulkPower

      if (startIndex === null || endIndex === null) {
        this.$bvToast.toast("Start and End indices are required", {
          title: "Error",
          variant: "danger"
        })
        return false
      }

      if (startIndex < 0 || startIndex > 95 || endIndex < 0 || endIndex > 95) {
        this.$bvToast.toast("Indices must be between 0 and 95", {
          title: "Error",
          variant: "danger"
        })
        return false
      }

      if (startIndex > endIndex) {
        this.$bvToast.toast(
          "Start index must be less than or equal to end index",
          {
            title: "Error",
            variant: "danger"
          }
        )
        return false
      }

      const hasUpValues = powerUp !== null && priceUp !== null

      if (!hasUpValues) {
        this.$bvToast.toast("Power Up and Price Up are required", {
          title: "Error",
          variant: "danger"
        })
        return false
      }

      return true
    },

    resetBulkPowerForm () {
      this.bulkPower = {
        startIndex: null,
        endIndex: null,
        powerUp: null,
        priceUp: null,
        powerDown: null,
        priceDown: null
      }
    },
    validateNewIndex () {
      const index = this.newPowerEntry.index

      if (index === null) {
        this.$bvToast.toast("Index is required", {
          title: "Error",
          variant: "danger"
        })
        return false
      }

      if (index < 0 || index > 95) {
        this.$bvToast.toast("Index must be between 0 and 95", {
          title: "Error",
          variant: "danger"
        })
        return false
      }

      if (this.form.power.some((p) => p.index === index)) {
        this.$bvToast.toast("This index is already in use", {
          title: "Error",
          variant: "danger"
        })
        return false
      }

      return true
    },

    handleAddPowerSubmit (event) {
      event.preventDefault()

      if (!this.validateNewIndex()) {
        return
      }

      this.form.power.push({
        ...this.newPowerEntry
      })

      this.form.power.sort((a, b) => a.index - b.index)

      this.newPowerEntry = {
        index: null,
        pUp: "",
        pDown: "",
        pUpPrice: "",
        pDownPrice: ""
      }
      this.showAddPowerModal = false
    },
    onPodToggled (data) {
      this.$emit('pod-toggled', {
        ...data,
        perimeterIndex: this.perimeterIndex
      })
    }
  }
}
</script>

<style scoped>
.table >>> thead {
  background-color: #af0f6a;
  color: white;
}

.table >>> th,
.table >>> td {
  vertical-align: middle;
}
</style>



// WEBPACK FOOTER //
// src/components/PerimeterForm.vue